import { getWithIDToken, IMSTokenParams } from "@utils/http/MSAuthAPI";
import { Country } from "@utils/constants/localisation";


interface ContactName {
  id: string;
  name: string;
};


interface ZohoContact {
  id: number;
  Owner: {
    name: string;
    id: string;
    email: string;
  };
  First_Name: string;
  Middle_Name: string | null;
  Last_Name: string;
  Email: string;
  Phone: string | null;
  Mobile: string | null;
  Date_of_Birth: string;
  Sex: string | null;
  Sex_Other: string | null;
  Alternate_Name: string;
  Street: string;
  Street_21: string | null;
  City: string;
  Months_at_address: number | null;
  Years_at_address: string | null;
  Postal_Code: string;
  Province: string | null;
  Drivers_License_Number: string;
  Drivers_License_Expiry_Date: string;
  Drivers_License_Jurisdiction: string;
  Name_1: string;
  Full_Address_1: string;
  Phone_1: string;
  Relationship_1: string;
  Nationality_New: string;
  ABN: string;
  Toll_Account_Number: string | null;
  Toll_Device_Number: string | null;
  Toll_Issuer: string | null;
  State: string;
  Mailing_Zip: string | null;
  Country: string[];
}

interface PotentialBase {
  id: string;
  Contact_Name: ContactName;
  Deal_Name: string;
  Stage: string;
  Test_Drive_Start: string;
  Test_Drive_End: string;
  Test_Drive_Vehicle: string;
  Test_Drive_Vehicle_Registration: string;
  Fleetio_Vehicle_ID: string;
  Trip_Discount: string;
  Minimum_Period: string;
  Weekly_Kilometre_Allowance: string;
  Weekly_Mileage_Allowance: string;
  Available_Vehicle_Discount: string;
  Created_Time: string;
  Vehicle: string;
};


interface PotentialAU extends PotentialBase {
  Splend_Initiative: string;
  Plan1: string;
  Plan: string;
};


interface PotentialGB extends PotentialBase {
  Plan: string;
};


type ZohoPotential = PotentialAU | PotentialGB;


export interface PricingRecordLineItem {
  pricing_template_line_item_id: number;
  chargebee_item_key: string;
  included: boolean;
  price_including_gst: number;
  price_excluding_gst: number;

  pricing_template_section_id: number;
  line_item_label_code: string;
  line_item_label_name: string;
  line_item_label_value: string;
  line_item_sorting_sequence: number;
  chargebee_component: string;
  chargebee_item_type: string;
  chargebee_item_name: string;
  coupon_duration_type?: string;
};


type ZohoSignQuoteDetail = {
  request_status: string;
  sign_submitted_time: string;
  action_time: string;
  expire_by: string;
  actions: {
    action_status: string;
    delivery_mode: string;
    recipient_email: string;
    recipient_name: string;
  }[];
};


export interface PricingRecordView {
  pricing_record_id: number;
  pricing_template_id?: number;
  status: string;

  country: string;
  state: string;
  plan: string;
  vehicle_condition: string;
  term_length: string;
  term_length_in_years: string;
  make: string;
  model: string;
  model_year: string;

  created_datetime: string;
  modified_datetime: string;
  completed_datetime: string;

  vehicle_colour: string;
  vehicle_registration: string;
  vehicle_delivery_eta: string;
  vehicle_driveaway_price: number;

  term_reduction: number;
  minimum_period_weeks: number;

  splend_servicing_limit: number;
  replacement_tyres: number;
  replacement_brake_pad_set: number;
  replacement_or_machining_of_brake_discs: number;

  weekly_distance_allowance: number;
  excess_distance_cost: number;

  setup_fee_including_gst: number;
  setup_fee_excluding_gst: number;

  initial_contribution: number | null;

  zoho_potential: ZohoPotential;
  zoho_contact: ZohoContact;

  zoho_sign_quote_detail: ZohoSignQuoteDetail;
  pricing_record_line_items: PricingRecordLineItem[];
  pricing_record_sections: any[];

  trip_count: string;
};


export const getPricingRecordsByPotential = async (
  country: Country,
  potentialId: string,
  IDTokenParams: IMSTokenParams,
): Promise<PricingRecordView> => {
    const { data } = await getWithIDToken(
        `/v1/pricing-record/potential/${country}/${potentialId}`,
        IDTokenParams,
    );

    const pricingRecord: PricingRecordView = {
        pricing_record_id: data.pricing_record.pricing_record_id,
        pricing_template_id: data.pricing_record.pricing_template_id,
        status: data.pricing_record.status,

        country: data.pricing_record.country,
        state: data.pricing_record.state,
        plan: data.pricing_record.plan,
        vehicle_condition: data.pricing_record.vehicle_condition,
        term_length: data.pricing_record.term_length,
        term_length_in_years: data.pricing_record.term_length_in_years,
        make: data.pricing_record.make,
        model: data.pricing_record.model,
        model_year: data.pricing_record.model_year,

        created_datetime: data.pricing_record.created_datetime,
        modified_datetime: data.pricing_record.modified_datetime,
        completed_datetime: data.pricing_record.completed_datetime,

        vehicle_colour: data.pricing_record.vehicle_colour,
        vehicle_registration: data.pricing_record.vehicle_registration,
        vehicle_delivery_eta: data.pricing_record.vehicle_delivery_eta,
        vehicle_driveaway_price: data.pricing_record.vehicle_driveaway_price,

        term_reduction: data.pricing_record.term_reduction,
        minimum_period_weeks: data.pricing_record.minimum_period_weeks,

        splend_servicing_limit: data.pricing_record.splend_servicing_limit,
        replacement_tyres: data.pricing_record.replacement_tyres,
        replacement_brake_pad_set: data.pricing_record.replacement_brake_pad_set,
        replacement_or_machining_of_brake_discs: data.pricing_record.replacement_or_machining_of_brake_discs,

        weekly_distance_allowance: data.pricing_record.weekly_distance_allowance,
        excess_distance_cost: data.pricing_record.excess_distance_cost,

        setup_fee_including_gst: data.pricing_record.setup_fee_including_gst,
        setup_fee_excluding_gst: data.pricing_record.setup_fee_excluding_gst,

        initial_contribution: data.pricing_record.initial_contribution ?? null,

        zoho_potential: data.pricing_record.zoho_potential,
        zoho_contact: data.pricing_record.zoho_contact,

        zoho_sign_quote_detail: {
          request_status: data.pricing_record.zoho_sign_quote_detail.request_status,
          sign_submitted_time: data.pricing_record.zoho_sign_quote_detail.sign_submitted_time,
          action_time: data.pricing_record.zoho_sign_quote_detail.action_time,
          expire_by: data.pricing_record.zoho_sign_quote_detail.expire_by,
          actions: data.pricing_record.zoho_sign_quote_detail.actions.map((action: any) => ({
            action_status: action.action_status,
            delivery_mode: action.delivery_mode,
            recipient_email: action.recipient_email,
            recipient_name: action.recipient_name,
          })),
        },

        pricing_record_line_items: (data.pricing_record.pricing_record_line_items || []).map((item: any): PricingRecordLineItem => ({
            pricing_template_line_item_id: item.pricing_template_line_item_id,
            chargebee_item_key: item.chargebee_item_key,
            included: item.included,
            price_including_gst: item.price_including_gst,
            price_excluding_gst: item.price_excluding_gst,
            pricing_template_section_id: item.pricing_template_section_id,
            line_item_label_code: item.line_item_label_code,
            line_item_label_name: item.line_item_label_name,
            line_item_label_value: item.line_item_label_value,
            line_item_sorting_sequence: item.line_item_sorting_sequence,
            chargebee_component: item.chargebee_component,
            chargebee_item_type: item.chargebee_item_type,
            chargebee_item_name: item.chargebee_item_name,
            coupon_duration_type: item.coupon_duration_type,
        })),

        pricing_record_sections: data.pricing_record.pricing_record_sections,
        trip_count: data.pricing_record.trip_count,
    };

    return pricingRecord;
};
